// Phase 5Z.11 — Driver Zeiterfassung (today's time entry)
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { Clock, Plus, Trash2, Eraser, Truck, AlertTriangle, Check, Calendar, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { fahrerApi } from "./lib/api";

const BREAK_TYPES = [
  { code: "RP", labelKey: "zeit.break_rp" },
  { code: "LP", labelKey: "zeit.break_lp" },
  { code: "SO", display: "SAZ", labelKey: "zeit.break_so" },
];

export default function FahrerZeiterfassung() {
  const { t } = useTranslation();
  const [today, setToday] = useState(undefined);
  const [step, setStep] = useState(0);  // 0 = guard screen, 1..3 = form steps
  const nav = useNavigate();

  // Form state
  const [workStart, setWorkStart] = useState("");
  const [workEnd, setWorkEnd] = useState("");
  const [drivingStart, setDrivingStart] = useState("");
  const [drivingEnd, setDrivingEnd] = useState("");
  const [startKm, setStartKm] = useState("");
  const [endKm, setEndKm] = useState("");
  const [workplace, setWorkplace] = useState("");
  const [startAddr, setStartAddr] = useState("");
  const [endAddr, setEndAddr] = useState("");
  const [notes, setNotes] = useState("");
  const [breaks, setBreaks] = useState([{ type: "RP", duration: 31 }]);
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function reload() {
    try {
      const { data } = await fahrerApi.get("/fahrer/today");
      setToday(data);
      // Pre-fill start_km from handover
      if (data.active_handover?.km && !startKm) {
        setStartKm(String(data.active_handover.km));
      }
      if (data.shift?.workplace && !workplace) setWorkplace(data.shift.workplace);
    } catch {
      setToday(null);
    }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  if (today === undefined) {
    return <div className="text-zinc-500 text-sm py-8 text-center">{t("app.loading")}</div>;
  }
  if (!today || !today.can_record) {
    return <ZeitGuard today={today} t={t} />;
  }

  // Form steps
  function next() {
    // Step 1: time + workplace
    if (step === 1) {
      if (!workplace.trim()) { toast.error(t("zeit.validation.workplace_required")); return; }
      if (!workStart || !workEnd) { toast.error(t("zeit.work_start") + " + " + t("zeit.work_end")); return; }
      if (workEnd <= workStart) { toast.error(t("zeit.validation.work_end_after_start")); return; }
      // Phase 5Z.13 — work_start cannot be before planned shift start
      const shiftStart = today.shift?.start_time || today.shift?.work_start || "";
      if (shiftStart && workStart < shiftStart) {
        toast.error(t("zeit.validation.work_start_before_shift", { time: shiftStart }));
        return;
      }
    }
    // Step 2: km + breaks
    if (step === 2) {
      if (!startKm || !endKm) { toast.error("KM"); return; }
      if (Number(endKm) <= Number(startKm)) { toast.error(t("zeit.validation.km_end_higher")); return; }
      const minKm = today.active_handover?.km || 0;
      if (Number(startKm) < minKm) { toast.error(t("zeit.validation.km_min_handover", { km: minKm })); return; }
      const rpTotal = breaks.filter(b => b.type === "RP").reduce((s, b) => s + Number(b.duration || 0), 0);
      if (rpTotal < 31) { toast.error(t("zeit.validation.rp_too_short")); return; }
    }
    setStep(step + 1);
  }

  async function submit() {
    if (!signature) { toast.error(t("zeit.validation.signature_required")); return; }
    setSubmitting(true);
    try {
      await fahrerApi.post("/fahrer/zeiterfassung", {
        date: today.today,
        work_start: workStart, work_end: workEnd,
        driving_start: drivingStart || null, driving_end: drivingEnd || null,
        start_km: Number(startKm), end_km: Number(endKm),
        workplace, start_address: startAddr, end_address: endAddr,
        notes,
        breaks: breaks.map(b => ({ type: b.type, duration: Number(b.duration), start: b.start || null, end: b.end || null })),
        signature_dataurl: signature,
      });
      toast.success(t("zeit.submitted_ok"));
      nav("/fahrer", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 0) {
    // Ready/intro screen
    return (
      <div className="space-y-4" data-testid="fahrer-zeit-ready">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-5">
          <Check className="w-8 h-8 text-emerald-400 mb-2" />
          <h2 className="text-base font-bold text-zinc-50">{t("zeit.ready")}</h2>
          <div className="mt-3 space-y-1.5 text-xs">
            <Field label={t("zeit.vehicle")} value={today.active_handover?.vehicle_label} />
            <Field label="KM " value={(today.active_handover?.km || 0).toLocaleString()} />
            <Field label={t("zeit.workplace")} value={today.shift?.workplace || "—"} />
          </div>
        </div>
        <button
          onClick={() => setStep(1)}
          data-testid="fahrer-zeit-start"
          className="w-full py-3.5 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg inline-flex items-center justify-center gap-2"
        >
          <Clock className="w-4 h-4" />
          {t("zeit.start_btn")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`fahrer-zeit-step-${step}`}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3.5">
        <div className="flex items-center gap-2 text-xs text-zinc-400 mb-2">
          <Clock className="w-4 h-4 text-amber-400" />
          {t("handover.step", { n: step, total: 3 })} · {today.active_handover?.vehicle_plate}
        </div>
        <div className="flex gap-1.5">
          {[1, 2, 3].map(n => (
            <div key={n} className={`flex-1 h-1 rounded ${n <= step ? "bg-amber-500" : "bg-zinc-800"}`} />
          ))}
        </div>
      </div>

      {step === 1 && (
        <Step1 t={t}
          workplace={workplace} setWorkplace={setWorkplace}
          workStart={workStart} setWorkStart={setWorkStart}
          workEnd={workEnd} setWorkEnd={setWorkEnd}
          drivingStart={drivingStart} setDrivingStart={setDrivingStart}
          drivingEnd={drivingEnd} setDrivingEnd={setDrivingEnd}
          startAddr={startAddr} setStartAddr={setStartAddr}
          endAddr={endAddr} setEndAddr={setEndAddr}
          shiftStart={today.shift?.start_time || today.shift?.work_start || ""}
        />
      )}
      {step === 2 && (
        <Step2 t={t}
          startKm={startKm} setStartKm={setStartKm} endKm={endKm} setEndKm={setEndKm}
          breaks={breaks} setBreaks={setBreaks}
          notes={notes} setNotes={setNotes}
          handoverKm={today.active_handover?.km}
        />
      )}
      {step === 3 && (
        <Step3 t={t} signature={signature} setSignature={setSignature}
          summary={{ workplace, workStart, workEnd, startKm, endKm, breaks, vehicle: today.active_handover?.vehicle_label }} />
      )}

      <div className="flex gap-2 pt-1">
        {step > 1 && (
          <button onClick={() => setStep(step - 1)} data-testid="fahrer-zeit-back"
            className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> {t("common.back")}
          </button>
        )}
        {step < 3 && (
          <button onClick={next} data-testid="fahrer-zeit-next"
            className="flex-1 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold">
            Weiter →
          </button>
        )}
        {step === 3 && (
          <button onClick={submit} disabled={submitting} data-testid="fahrer-zeit-submit"
            className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-800 text-white rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5">
            <Check className="w-4 h-4" />
            {submitting ? "…" : t("zeit.submit")}
          </button>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Guard screen (no shift OR no handover OR already submitted)
// ────────────────────────────────────────────────────────────
function ZeitGuard({ today, t }) {
  if (!today) return <div className="text-zinc-500 text-sm">{t("common.error")}</div>;
  let icon = AlertTriangle;
  let cls = "bg-amber-500/10 border-amber-500/30 text-amber-300";
  let title = t("zeit.no_shift");
  let hint = t("zeit.no_shift_hint");
  let cta = null;

  if (!today.has_shift) {
    title = t("zeit.no_shift");
    hint = t("zeit.no_shift_hint");
  } else if (!today.active_handover || today.active_handover.status !== "finalized") {
    icon = Truck;
    title = t("zeit.no_handover");
    hint = t("zeit.no_handover_hint");
    cta = { to: "/fahrer/vehicle", label: t("handover.start_uebernahme") };
  } else if (today.time_entry) {
    cls = "bg-emerald-500/10 border-emerald-500/30 text-emerald-300";
    icon = Check;
    title = t("zeit.already_submitted");
    hint = t("zeit.already_submitted_hint");
  }

  const Icon = icon;
  return (
    <div className="space-y-4" data-testid="fahrer-zeit-guard">
      <div>
        <h1 className="text-xl font-bold text-zinc-50">{t("zeit.today_title")}</h1>
      </div>
      <div className={`${cls} border rounded-lg p-5 flex gap-3 items-start`}>
        <Icon className="w-5 h-5 shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-bold text-zinc-50">{title}</h3>
          <p className="text-xs mt-1 opacity-90">{hint}</p>
          {cta && (
            <Link to={cta.to} data-testid="fahrer-zeit-cta" className="inline-block mt-3 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded">
              {cta.label}
            </Link>
          )}
        </div>
      </div>
      <Link to="/fahrer/calendar" data-testid="fahrer-zeit-cal-link" className="block bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-amber-600/50">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-amber-400" />
          <div className="flex-1">
            <div className="text-sm font-medium text-zinc-100">{t("zeit.calendar_title")}</div>
            <div className="text-[10px] text-zinc-500">{t("nav.schedule")}</div>
          </div>
        </div>
      </Link>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Steps
// ────────────────────────────────────────────────────────────
function Step1({ t, workplace, setWorkplace, workStart, setWorkStart, workEnd, setWorkEnd, drivingStart, setDrivingStart, drivingEnd, setDrivingEnd, startAddr, setStartAddr, endAddr, setEndAddr, shiftStart }) {
  return (
    <div className="space-y-3">
      <Input label={`${t("zeit.workplace")} *`} value={workplace} onChange={setWorkplace} placeholder={t("zeit.workplace_placeholder")} testid="zeit-workplace" />
      <div className="grid grid-cols-2 gap-2">
        <Input label={`${t("zeit.work_start")} *`} type="time" value={workStart} onChange={setWorkStart} testid="zeit-work-start" min={shiftStart || undefined} />
        <Input label={`${t("zeit.work_end")} *`} type="time" value={workEnd} onChange={setWorkEnd} testid="zeit-work-end" />
        <Input label={t("zeit.driving_start")} type="time" value={drivingStart} onChange={setDrivingStart} testid="zeit-driving-start" />
        <Input label={t("zeit.driving_end")} type="time" value={drivingEnd} onChange={setDrivingEnd} testid="zeit-driving-end" />
      </div>
      {shiftStart && (
        <div className="text-[11px] text-amber-400 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1.5" data-testid="zeit-shift-hint">
          {t("zeit.shift_hint", { time: shiftStart })}
        </div>
      )}
      <Input label={t("zeit.start_address")} value={startAddr} onChange={setStartAddr} testid="zeit-start-addr" />
      <Input label={t("zeit.end_address")} value={endAddr} onChange={setEndAddr} testid="zeit-end-addr" />
    </div>
  );
}

function Step2({ t, startKm, setStartKm, endKm, setEndKm, breaks, setBreaks, notes, setNotes, handoverKm }) {
  function addBreak() {
    setBreaks([...breaks, { type: "LP", duration: 15 }]);
  }
  function update(idx, key, value) {
    setBreaks(breaks.map((b, i) => i === idx ? { ...b, [key]: value } : b));
  }
  function remove(idx) {
    setBreaks(breaks.filter((_, i) => i !== idx));
  }
  const rpTotal = breaks.filter(b => b.type === "RP").reduce((s, b) => s + Number(b.duration || 0), 0);
  const rpOk = rpTotal >= 31;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Input label={`${t("zeit.start_km")} *`} type="number" inputMode="numeric" value={startKm} onChange={setStartKm} testid="zeit-start-km" />
        <Input label={`${t("zeit.end_km")} *`} type="number" inputMode="numeric" value={endKm} onChange={setEndKm} testid="zeit-end-km" />
      </div>
      {handoverKm > 0 && (
        <p className="text-[10px] text-zinc-500">KM bei Übernahme: <span className="font-mono text-zinc-300">{handoverKm.toLocaleString()}</span></p>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("zeit.breaks")} *</label>
          <button onClick={addBreak} data-testid="zeit-add-break" className="text-xs px-2 py-1 bg-zinc-800 hover:bg-amber-600 text-zinc-100 rounded inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> {t("zeit.add_break")}
          </button>
        </div>
        <p className="text-[10px] text-zinc-500 mb-2">{t("zeit.breaks_hint")}</p>
        <div className="space-y-1.5">
          {breaks.map((b, i) => (
            <div key={i} className="flex gap-1.5 items-center bg-zinc-900 border border-zinc-800 rounded p-2" data-testid={`zeit-break-${i}`}>
              <select value={b.type} onChange={(e) => update(i, "type", e.target.value)} data-testid={`zeit-break-${i}-type`}
                className="px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs">
                {BREAK_TYPES.map(bt => <option key={bt.code} value={bt.code}>{bt.display || bt.code}</option>)}
              </select>
              <input type="number" inputMode="numeric" value={b.duration} onChange={(e) => update(i, "duration", e.target.value)}
                data-testid={`zeit-break-${i}-dur`}
                className="w-16 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs font-mono" />
              <span className="text-[10px] text-zinc-500">min</span>
              <button onClick={() => remove(i)} data-testid={`zeit-break-${i}-del`} className="ml-auto p-1.5 text-zinc-500 hover:text-red-400">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className={`mt-2 text-xs ${rpOk ? "text-emerald-400" : "text-amber-400"}`} data-testid="zeit-rp-total">
          {t("zeit.rp_total")}: {rpTotal} min {rpOk ? "✓" : "(min. 31 erforderlich)"}
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("zeit.notes")}</label>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="zeit-notes"
          className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-lg text-sm" />
      </div>
    </div>
  );
}

function Step3({ t, signature, setSignature, summary }) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);

  function getPoint(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const touch = e.touches?.[0];
    return {
      x: ((touch?.clientX ?? e.clientX) - rect.left) * (c.width / rect.width),
      y: ((touch?.clientY ?? e.clientY) - rect.top) * (c.height / rect.height),
    };
  }
  function start(e) { e.preventDefault(); isDrawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = getPoint(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); }
  function move(e) { if (!isDrawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = getPoint(e); ctx.lineTo(p.x, p.y); ctx.stroke(); }
  function end() { if (!isDrawing.current) return; isDrawing.current = false; setSignature(canvasRef.current.toDataURL("image/png")); }
  function clear() { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setSignature(null); }
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d"); ctx.strokeStyle = "#fff"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round";
  }, []);

  return (
    <div className="space-y-3" data-testid="fahrer-zeit-signature-step">
      <h3 className="text-base font-semibold text-zinc-100">{t("zeit.review_title")}</h3>
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3.5 space-y-1.5 text-xs">
        <Field label={t("zeit.vehicle")} value={summary.vehicle} />
        <Field label={t("zeit.workplace")} value={summary.workplace} />
        <Field label={t("zeit.work_start") + " / " + t("zeit.work_end")} value={`${summary.workStart} – ${summary.workEnd}`} />
        <Field label="KM" value={`${summary.startKm} → ${summary.endKm}`} />
        <Field label={t("zeit.breaks")} value={summary.breaks.map(b => `${b.type} ${b.duration}min`).join(" · ")} />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("handover.signature_title")} *</label>
        <p className="text-[10px] text-zinc-500 mt-0.5 mb-2">{t("handover.signature_hint")}</p>
        <div className="bg-zinc-900 border-2 border-dashed border-zinc-700 rounded-lg p-2">
          <canvas ref={canvasRef} width={760} height={260} data-testid="fahrer-zeit-signature-canvas"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            className="w-full h-40 bg-zinc-950 rounded touch-none" />
        </div>
        <button onClick={clear} data-testid="fahrer-zeit-sig-clear" className="mt-2 text-xs text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1">
          <Eraser className="w-3 h-3" /> {t("handover.signature_clear")}
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid, inputMode, min }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        inputMode={inputMode}
        min={min}
        className="w-full mt-1 px-3 py-2.5 bg-zinc-950 border border-zinc-800 rounded-lg text-sm font-mono"
      />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-100 font-mono text-right truncate">{value || "—"}</span>
    </div>
  );
}
